import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { SUPABASE_URL } from "@/constants/supabase";
import { useToast } from "@/components/ui/use-toast";

const formSchema = z.object({
  email: z.string().email({
    message: "Please enter a valid email address.",
  }),
  reason: z.string().optional(),
  confirmUnsubscribe: z.boolean().refine((val) => val === true, {
    message: "You must confirm that you want to unsubscribe.",
  }),
});

export default function EmailUnsubscribe() {
  const [searchParams] = useSearchParams();
  const emailParam = searchParams.get("email") || "";
  const tokenParam = searchParams.get("token") || "";
  const typeParam = searchParams.get("type") || "all"; // all, marketing, service, etc.
  const { toast } = useToast();

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [email, setEmail] = useState(emailParam);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: emailParam,
      reason: "",
      confirmUnsubscribe: false,
    },
  });

  // Get unsubscribe type text
  const getUnsubscribeTypeText = () => {
    switch (typeParam) {
      case "marketing":
        return "marketing emails";
      case "service":
        return "service update emails";
      case "document":
        return "document request emails";
      case "information":
        return "information request emails";
      default:
        return "all emails";
    }
  };

  // Process unsubscribe automatically if token and email are provided
  useEffect(() => {
    if (tokenParam && emailParam) {
      processUnsubscribe(emailParam, tokenParam, typeParam);
    }
  }, [tokenParam, emailParam, typeParam]);

  // Function to process unsubscribe
  async function processUnsubscribe(email: string, token: string, type: string) {
    setIsLoading(true);
    setError(null);

    try {
      // Call the unsubscribe endpoint
      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/email-preferences/unsubscribe`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${supabase.auth.getSession()}`,
          },
          body: JSON.stringify({ email, token, type }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to process unsubscribe request");
      }

      setIsSubmitted(true);
      toast({
        title: "Unsubscribed Successfully",
        description: `You have been unsubscribed from ${getUnsubscribeTypeText()}.`,
      });
    } catch (err) {
      console.error("Error processing unsubscribe:", err);
      setError(
        err instanceof Error ? err.message : "An unknown error occurred"
      );
    } finally {
      setIsLoading(false);
    }
  }

  // Function to handle form submission
  async function onSubmit(values: z.infer<typeof formSchema>) {
    setIsLoading(true);
    setError(null);

    try {
      // For manual unsubscribe (no token), we'll use a different approach
      // In a real implementation, this would send an email with a confirmation link
      // For demo purposes, we'll just simulate the unsubscribe
      
      // Get token from localStorage if available (for demo purposes)
      const tokenToUse = tokenParam || "demo-token";

      // Call the unsubscribe endpoint
      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/email-preferences/unsubscribe`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${supabase.auth.getSession()}`,
          },
          body: JSON.stringify({
            email: values.email,
            token: tokenToUse,
            type: typeParam,
            reason: values.reason,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to process unsubscribe request");
      }

      setIsSubmitted(true);
      toast({
        title: "Unsubscribed Successfully",
        description: `You have been unsubscribed from ${getUnsubscribeTypeText()}.`,
      });
    } catch (err) {
      console.error("Error processing unsubscribe:", err);
      setError(
        err instanceof Error ? err.message : "Failed to unsubscribe"
      );
      toast({
        variant: "destructive",
        title: "Error",
        description:
          err instanceof Error
            ? err.message
            : "Failed to unsubscribe. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="container max-w-2xl py-10">
      <div className="mb-8 text-center">
        <h1 className="text-3xl font-bold mb-2">Unsubscribe</h1>
        <p className="text-muted-foreground">
          Manage your subscription to {getUnsubscribeTypeText()}
        </p>
      </div>

      {isLoading && !error && !isSubmitted ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-10">
            <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
            <p className="text-center text-muted-foreground">
              Processing your unsubscribe request...
            </p>
          </CardContent>
        </Card>
      ) : error ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Error
            </CardTitle>
            <CardDescription>
              We encountered a problem processing your unsubscribe request.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">{error}</p>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Button
                variant="outline"
                onClick={() => window.location.reload()}
              >
                Try Again
              </Button>
              <Button
                variant="default"
                onClick={() => {
                  window.location.href = "/email-preference";
                }}
              >
                Manage Preferences Instead
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : isSubmitted ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-500" />
              Unsubscribed Successfully
            </CardTitle>
            <CardDescription>
              You have been unsubscribed from {getUnsubscribeTypeText()}.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              You will no longer receive these emails from Numberz. If
              you change your mind, you can update your preferences at any time.
            </p>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Button
                variant="outline"
                onClick={() => (window.location.href = "/email-preference")}
              >
                Manage All Preferences
              </Button>
              <Button
                variant="default"
                onClick={() => {
                  window.location.href = `/email-preference?email=${encodeURIComponent(
                    email
                  )}`;
                }}
              >
                Update Preferences
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <Alert variant="destructive" className="text-white dark:text-white">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle className="text-white dark:text-white">
                You are about to unsubscribe
              </AlertTitle>
              <AlertDescription className="text-white dark:text-white">
                You will no longer receive {getUnsubscribeTypeText()} from
                Numberz.
              </AlertDescription>
            </Alert>

            <Card>
              <CardHeader>
                <CardTitle>Confirm Unsubscribe</CardTitle>
                <CardDescription>
                  Please confirm that you want to unsubscribe from{" "}
                  {getUnsubscribeTypeText()}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email Address</FormLabel>
                      <FormControl>
                        <input
                          type="email"
                          className="w-full p-2 border rounded-md bg-gray-50 dark:bg-gray-800 border-gray-300 dark:border-gray-700"
                          placeholder="your@email.com"
                          {...field}
                          disabled={!!emailParam}
                          onChange={(e) => {
                            field.onChange(e);
                            setEmail(e.target.value);
                          }}
                        />
                      </FormControl>
                      <FormDescription>
                        The email address you want to unsubscribe
                      </FormDescription>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="reason"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Reason (Optional)</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Please tell us why you're unsubscribing..."
                          className="resize-none"
                          {...field}
                        />
                      </FormControl>
                      <FormDescription>
                        Your feedback helps us improve our communications
                      </FormDescription>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="confirmUnsubscribe"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4 mt-4">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <div className="space-y-1 leading-none">
                        <FormLabel>
                          I confirm that I want to unsubscribe from{" "}
                          {getUnsubscribeTypeText()}
                        </FormLabel>
                        <FormDescription>
                          You can resubscribe at any time by updating your
                          preferences
                        </FormDescription>
                      </div>
                    </FormItem>
                  )}
                />
              </CardContent>
              <CardFooter className="flex justify-between">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => (window.location.href = "/email-preference")}
                >
                  Manage All Preferences Instead
                </Button>
                <Button type="submit" variant="destructive" disabled={isLoading}>
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    "Unsubscribe"
                  )}
                </Button>
              </CardFooter>
            </Card>
          </form>
        </Form>
      )}
    </div>
  );
}
